import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ReactComponent as CloseIcon } from 'assets/icons/Clear.svg';
import { ReactComponent as FcaLogo } from 'assets/icons/FcaLogoBlue.svg';
import { ReactComponent as WelcomeGraphic } from 'assets/images/WelcomeGraphic.svg';
import { STYLE } from 'constants/style';
import { ROUTES } from 'constants/routes';

interface WelcomeModalProps {
  open: boolean;
  onClose: () => void;
  projectName: string;
  isCertificationComplete: boolean;
  isCoursesComplete: boolean;
  isDrugTestingComplete: boolean;
  isUnverified?: boolean;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({
  open,
  onClose,
  projectName,
  isCertificationComplete,
  isCoursesComplete,
  isDrugTestingComplete,
  isUnverified,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLinkClick = (link?: string) => {
    if (link) {
      onClose();
      navigate(link);
    }
  };

  const bulletPoints = [
    {
      key: 'certifications',
      text: (
        <>
          {t('welcomeModal.bullets.certifications.part1')}{' '}
          <span className={classes.link} onClick={() => handleLinkClick(ROUTES.MY_CERTIFICATIONS.path)}>
            {t('welcomeModal.bullets.certifications.link')}
          </span>{' '}
          {t('welcomeModal.bullets.certifications.part2')}
        </>
      ),
      show: !isCertificationComplete,
    },
    {
      key: 'courses',
      text: (
        <>
          {t('welcomeModal.bullets.courses.part1')}{' '}
          <span className={classes.link} onClick={() => handleLinkClick(ROUTES.MY_COURSES.path)}>
            {t('welcomeModal.bullets.courses.link')}
          </span>{' '}
          {t('welcomeModal.bullets.courses.part2')}
        </>
      ),
      show: !isCoursesComplete,
    },
    {
      key: 'drugTesting',
      text: (
        <>
          {t('welcomeModal.bullets.drugTesting.part1')}{' '}
          <span className={classes.link} onClick={() => handleLinkClick(ROUTES.MY_DRUG_TESTING.path)}>
            {t('welcomeModal.bullets.drugTesting.link')}
          </span>
          .
        </>
      ),
      show: !isDrugTestingComplete,
    },
    {
      key: 'workerProfile',
      text: (
        <>
          <span className={classes.link} onClick={() => handleLinkClick(ROUTES.PROJECT_LIST.path)}>
            {t('welcomeModal.loginLink')}
          </span>{' '}
          {t('welcomeModal.loginDescription')}
        </>
      ),
      show: true,
    },
  ];

  const visibleBulletPoints = bulletPoints.filter(point => point.show);
  const hasMultipleBullets = visibleBulletPoints.length > 1;

  return (
    <Dialog disableScrollLock open={open} onClose={onClose} className={classes.dialog}>
      <div className={classes.header}>
        <FcaLogo />
        <div className={classes.closeButton} onClick={onClose} data-testid="welcome-modal-close-button">
          <CloseIcon />
        </div>
      </div>
      <DialogContent className={classes.content}>
        <div className={classes.illustrationContainer}>
          <WelcomeGraphic />
        </div>
        <h2 className={classes.title}>{t('welcomeModal.title')}</h2>
        <p className={classes.projectName}>
          {t('welcomeModal.assignedTo.part1')} <strong>{projectName}</strong>
        </p>
        {!isUnverified && hasMultipleBullets && <p className={classes.description}>{t('welcomeModal.description')}</p>}
        {!isUnverified && (
          <ul className={hasMultipleBullets ? classes.bulletList : classes.singleItem}>
            {visibleBulletPoints.map(point => (
              <li key={point.key} className={classes.bulletItem}>
                {point.text}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      width: '100%',
      margin: '16px',
      position: 'relative',
      overflow: 'hidden',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px dashed ${STYLE.COLOR.ACCENT_PRIMARY_LIGHTER3}`,
  },
  closeButton: {
    padding: '4px',
    color: STYLE.COLOR.TEXT1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    padding: '32px 32px 36px !important',
    overflow: 'visible',
    textAlign: 'center',
  },
  illustrationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '28px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: STYLE.COLOR.ACCENT_PRIMARY,
    textAlign: 'center',
    margin: 0,
    marginBottom: '8px',
  },
  projectName: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: STYLE.COLOR.TEXT1,
    textAlign: 'center',
    marginTop: 0,
    marginBottom: '24px',
    '& strong': {
      color: STYLE.COLOR.ACCENT_PRIMARY,
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  description: {
    fontSize: '16px',
    fontWeight: 400,
    color: STYLE.COLOR.TEXT2,
    lineHeight: '24px',
    textAlign: 'left',
    marginTop: 0,
    marginBottom: '8px',
  },
  bulletList: {
    listStyle: 'disc',
    paddingLeft: '28px',
    margin: 0,
    textAlign: 'left',
    '& li': {
      paddingLeft: '8px',
    },
  },
  singleItem: {
    listStyle: 'none',
    paddingLeft: 0,
    margin: 0,
    textAlign: 'center',
  },
  bulletItem: {
    fontSize: '16px',
    fontWeight: 400,
    color: STYLE.COLOR.TEXT1,
    lineHeight: '24px',
    marginBottom: '4px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  link: {
    color: STYLE.COLOR.ACCENT_PRIMARY,
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 500,
  },
}));
